import { Menu, Plus, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { ConversationListItem } from './ConversationListItem';
import { Input } from './Input';

import type { ConversationListItemProps } from './ConversationListItem';

export interface ConversationDrawerProps {
  /** Whether the drawer is currently open */
  open: boolean;
  /** Callback to toggle drawer open/close */
  onOpenChange: (open: boolean) => void;
  /** List of conversations to display */
  conversations: ConversationListItemProps[];
  /** Callback when a conversation is selected */
  onNavigate?: (conversationId: string) => void;
  /** Callback to create a new conversation */
  onNewConversation?: () => void;
  /** ID of the currently active conversation */
  activeConversationId?: string;
  /** Additional CSS classes */
  className?: string;
}

export function ConversationDrawer({
  open,
  onOpenChange,
  conversations,
  onNavigate,
  onNewConversation,
  activeConversationId,
  className = '',
}: ConversationDrawerProps) {
  const [searchText, setSearchText] = useState('');
  const drawerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onOpenChange(false);
      }
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open || !drawerRef.current) return;
    const el = drawerRef.current;
    const focusable = el.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    }
    el.addEventListener('keydown', handleKeyDown);
    return () => el.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  const filtered = searchText
    ? conversations.filter((c) => c.title.toLowerCase().includes(searchText.toLowerCase()))
    : conversations;

  return (
    <div className={className}>
      <button
        aria-expanded={open}
        aria-controls="conversation-drawer"
        aria-label="Open conversations"
        onClick={() => onOpenChange(true)}
      >
        <Menu size={24} />
      </button>
      {open && (
        <div
          data-testid="scrim"
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
          onClick={() => onOpenChange(false)}
        />
      )}
      <div
        ref={drawerRef}
        id="conversation-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Conversations"
        className={`fixed left-0 top-0 h-full w-[280px] bg-sd-surface shadow-sd-lg border-r border-sd-border/30 z-50 flex flex-col transition-transform duration-200 ease-out motion-reduce:transition-none ${open ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="flex items-center justify-between p-sd-2 border-b border-sd-border/30 pb-sd-2">
          <span className="text-sd-sm font-semibold tracking-wide uppercase text-sd-text">Conversations</span>
          <div className="flex items-center gap-sd-1">
            {onNewConversation && (
              <button
                aria-label="New conversation"
                className="p-sd-1 rounded-sd-md hover:bg-sd-warning/10 transition-colors duration-150"
                onClick={onNewConversation}
              >
                <Plus size={20} />
              </button>
            )}
            <button
              aria-label="Close conversations"
              className="p-sd-1 rounded-sd-md hover:bg-sd-surface-elevated transition-colors duration-150"
              autoFocus={open}
              onClick={() => onOpenChange(false)}
            >
              <X size={20} />
            </button>
          </div>
        </div>
        <div className="px-sd-2 pb-sd-2 transition-colors duration-150">
          <Input
            id="drawer-search"
            variant="search"
            placeholder="Search conversations..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
        <div className="flex-1 overflow-y-auto flex flex-col">
          {filtered.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center p-sd-3">
              <p className="text-sd-base font-medium text-sd-text mb-sd-1">No conversations yet</p>
              <p className="text-sd-sm text-sd-text-muted">Start a conversation to get going.</p>
            </div>
          ) : (
            <ul role="listbox" aria-label="Conversations">
              {filtered.map((c) => (
                <ConversationListItem
                  key={c.id}
                  {...c}
                  active={c.id === activeConversationId}
                  onClick={(id) => {
                    onNavigate?.(id);
                    onOpenChange(false);
                  }}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
